import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQueries } from '@tanstack/react-query';
import { memo } from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItemList,
    SidebarLink,
} from '@gitbook/spine-react';

import {
    APIErrorBoundary,
    NewFeatureBadge,
    NotificationsBell,
    OrganizationSearchButton,
} from '../../Components';
import { apiQuery } from '../../state';
import { CustomerIOAlert } from './CustomerIOAlert';
import { FeedbackButton } from './FeedbackButton';
import { HelpButton } from './HelpButton';
import { OrganizationAlert } from './OrganizationAlert';
import { OrganizationButton } from './OrganizationButton';
import { SitesDisclosure } from './Sites';
import { SpacesDisclosure } from './Spaces';
import { useAutoExpandCollections } from './useAutoExpandCollections';

interface ContentSidebarProps {
    screen: NavigationScreenState;
}

export function ContentSidebar(props: ContentSidebarProps) {
    return (
        <Sidebar>
            <ContentSidebarContent {...props} />
        </Sidebar>
    );
}

function ContentSidebarContent(props: ContentSidebarProps) {
    const { screen } = props;

    if (!('organization' in screen) || !screen.organization) {
        return <NoOrganizationEmptyState />;
    }

    return (
        <APIErrorBoundary
            fallback={{
                404: <NoOrganizationEmptyState />,
                403: <NoOrganizationEmptyState />,
            }}
        >
            <ContentSidebarContentWithOrg screen={screen} organizationId={screen.organization} />
        </APIErrorBoundary>
    );
}

function NoOrganizationEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Organization />
            </EmptyStateIcon>
            <Description>
                Join an organization or create a new one to get started with your content.
            </Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    label="Create organization"
                    href={{ screen: { type: 'welcome' }, overlay: null }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Memoized content of the sidebar, optimized to re-render only when relevant url parts change.
 */
function ContentSidebarContentWithOrg(props: {
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen } = props;
    const [{ data: organization }, { data: orgAllContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });

    useAutoExpandCollections({ orgAllContent, screen });

    return (
        <>
            <ContentSidebarHeader organization={organization} />
            <SidebarBody aria-label="Main navigation" className="pb-10">
                <SidebarItemList>
                    <HomeLink screen={screen} />
                    <SitesLink screen={screen} />
                    <ChangeRequestsLink screen={screen} />
                    <AgentsLink screen={screen} />
                    <OpenAPILink screen={screen} />
                    <TranslationsLink screen={screen} />
                    <IntegrationsLink screen={screen} />
                </SidebarItemList>
                <SitesDisclosure
                    screen={screen}
                    content={orgAllContent}
                    organization={organization}
                />
                <SpacesDisclosure
                    content={orgAllContent}
                    organization={organization}
                    screen={screen}
                />
            </SidebarBody>
            <ContentSidebarFooter organization={organization} />
        </>
    );
}

const ContentSidebarHeader = memo(function ContentSidebarHeader(props: {
    organization: api.Organization;
}) {
    const { organization } = props;
    return (
        <SidebarHeader>
            <div className="flex flex-1">
                <OrganizationButton organization={organization} />
            </div>
            <div className="flex items-center gap-0.5">
                <OrganizationSearchButton
                    data-testid="sidebar.findButton"
                    organization={organization}
                />
                <NotificationsBell />
            </div>
        </SidebarHeader>
    );
});

const ContentSidebarFooter = memo(function ContentSidebarFooter(props: {
    organization: api.Organization;
}) {
    const { organization } = props;
    return (
        <>
            <SidebarFooter className="pb-8 [&:has(>#customerio-alert:only-child:empty)]:hidden">
                <OrganizationAlert data-org-alerts organization={organization} />
                <CustomerIOAlert />
            </SidebarFooter>
            <div className="absolute inset-x-2 bottom-2 flex items-center gap-1 bg-gradient-to-t from-30% from-[--sp-color-bg-side] to-transparent">
                <HelpButton />
                <FeedbackButton />
            </div>
        </>
    );
});

function HomeLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Home"
            icon={Icon.Grid}
            isActive={screen.type === 'home'}
            href={{
                screen: {
                    type: 'home',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function SitesLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Docs sites"
            icon={Icon.DocsSite}
            isActive={screen.type === 'sites'}
            href={{
                screen: {
                    type: 'sites',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function OpenAPILink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="OpenAPI"
            icon={Icon.OpenAPI}
            isActive={screen.type === 'openapi_spec' || screen.type === 'openapi_specs'}
            href={{
                screen: {
                    type: 'openapi_specs',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function TranslationsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Translations"
            icon={Icon.AITranslate}
            isActive={screen.type === 'translations'}
            href={{
                screen: {
                    type: 'translations',
                    organization: 'organization' in screen ? screen.organization : undefined,
                    view: 'overview',
                },
            }}
        >
            <NewFeatureBadge flagKey="TRANSLATIONS" />
        </SidebarLink>
    );
}

function ChangeRequestsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    const feature = useFlag('CHANGE_REQUESTS_SCREEN');
    if (!feature.isEnabled) {
        return null;
    }

    return (
        <SidebarLink
            label="Change Requests"
            icon={Icon.ChangeRequest}
            isActive={screen.type === 'change_requests'}
            href={{
                screen: {
                    type: 'change_requests',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        >
            <NewFeatureBadge flagKey="CHANGE_REQUESTS_SCREEN" />
        </SidebarLink>
    );
}

function AgentsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;

    return (
        <SidebarLink
            label="GitBook Agent"
            icon={Icon.AIAgent}
            isActive={screen.type === 'agents'}
            href={{
                screen: {
                    type: 'agents',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        >
            <NewFeatureBadge flagKey="DOCS_AGENTS_CONVERSATIONS" />
        </SidebarLink>
    );
}

function IntegrationsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Integrations"
            icon={Icon.Integrations}
            isActive={screen.type === 'integrations'}
            href={{
                screen: {
                    type: 'integrations',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}
